import resourceBaselines from '../data/resource-baselines.json';
import resourceOverrides from '../data/resource-overrides.json';
import productServiceMapping from '../data/product-service-mapping.json';

const psmProducts = (productServiceMapping as any).products as Record<string, { type: string; services: { name: string; displayName: string }[] }>;

/**
 * Maps graph service names to helm chart names used in resource-baselines.json.
 * Only includes entries where the names differ.
 */
export const GRAPH_TO_HELM: Record<string, string> = {
  platform: 'platform-services',
  sfcore: 'service-fabric-core',
  integratedauth: 'integrated-auth',
  taas: 'taas-meta',
  conversationalagentsservice: 'autopilot-everyone',
  integrationservices: 'integration-services',
  processmining: 'process-mining-meta',
  apps: 'business-apps',
  documentunderstanding: 'du',
  webhook: 'webhook-service',
  'sa-patcher': 'wi-sa-patcher',
  automation_ops: 'studio-governance',
  action_center: 'actioncenter',
  automation_hub: 'automationhub',
  test_manager: 'testmanager',
};

/**
 * Parse a CPU string like "1.13" to a float.
 */
export function parseCpu(s: string): number {
  return parseFloat(s) || 0;
}

/**
 * Parse a RAM string like "1.30Gi" or "512Mi" to a float in GiB.
 */
export function parseRam(s: string): number {
  if (s.endsWith('Gi')) {
    return parseFloat(s.replace('Gi', '')) || 0;
  }
  if (s.endsWith('Mi')) {
    return (parseFloat(s.replace('Mi', '')) || 0) / 1024;
  }
  return parseFloat(s) || 0;
}

interface ResourceEntry {
  // Only workload_cpu/workload_ram are consumed by the sizing math; the
  // remaining fields mirror the upstream resource-baselines schema.
  workload_cpu: string;
  workload_ram: string;
  job_cpu: string;
  job_ram: string;
  // Optional: present in upstream baseline entries, absent from the
  // calculator-only as-portal-backend chart.
  max_workload_cpu?: string;
  max_workload_ram?: string;
  workload_limit_cpu?: string;
  workload_limit_ram?: string;
  max_workload_limit_cpu?: string;
  max_workload_limit_ram?: string;
  job_limit_cpu?: string;
  job_limit_ram?: string;
}

/**
 * Look up resource values for a graph service name.
 * Returns { cpu, ram } in millicores and MiB.
 *
 * Checks resource-overrides.json first (takes precedence), then resource-baselines.json.
 */
export function getServiceResources(
  graphServiceName: string,
  distribution: string,
  profile: string
): { cpu: number; ram: number } {
  const helmName = GRAPH_TO_HELM[graphServiceName] || graphServiceName;

  // Check overrides first (keyed by graph name, not helm name)
  let entry = lookupEntry(resourceOverrides, graphServiceName, distribution, profile);

  // Then check baselines (keyed by helm chart name)
  if (!entry) {
    entry = lookupEntry(resourceBaselines, helmName, distribution, profile);
  }

  if (!entry) {
    return { cpu: 0, ram: 0 };
  }

  const cpuCores = parseCpu(entry.workload_cpu);
  const ramGi = parseRam(entry.workload_ram);

  // Round each service to 2 decimals in display units (vCPU, GiB) *before* it enters the
  // totals, so the value shown per service is exactly the value summed — no finer
  // millicore/MiB granularity that would make line items not add up to the total.
  return {
    cpu: Math.round(cpuCores * 100) * 10,        // 2-decimal vCPU, expressed in millicores
    ram: (Math.round(ramGi * 100) / 100) * 1024, // 2-decimal GiB, expressed in MiB
  };
}

/**
 * Compute aggregate resource requirements for a product by summing its services
 * from product-service-mapping.json.
 */
export function getProductResources(
  productKey: string,
  distribution: string,
  baselineProfile: string
): { cpu: number; ram: number } {
  const def = psmProducts[productKey];
  if (!def) return { cpu: 0, ram: 0 };

  let totalCpu = 0;
  let totalRam = 0;
  for (const svc of def.services) {
    const res = getServiceResources(svc.name, distribution, baselineProfile);
    totalCpu += res.cpu;
    totalRam += res.ram;
  }
  return { cpu: totalCpu, ram: totalRam };
}

/**
 * Look up product-level override from resource-overrides.json.
 * Overrides are keyed by product key (currently only 'fabric'),
 * NOT by graph service name.
 * Returns { cpu, ram } in millicores/MiB, or null if no override exists.
 */
export function getProductOverride(
  productKey: string,
  distribution: string,
  profile: string
): { cpu: number; ram: number } | null {
  const entry = lookupEntry(resourceOverrides, productKey, distribution, profile);
  if (!entry) return null;
  // Same 2-decimal rounding as getServiceResources so product-level overrides stay
  // consistent with the per-service values and the totals.
  const cpu = Math.round(parseCpu(entry.workload_cpu || '0') * 100) * 10;
  const ram = (Math.round(parseRam(entry.workload_ram || '0Gi') * 100) / 100) * 1024;
  return { cpu, ram };
}

function lookupEntry(
  data: any,
  serviceName: string,
  distribution: string,
  profile: string
): ResourceEntry | null {
  const svc = data[serviceName];
  if (!svc) return null;
  const dist = svc[distribution];
  if (!dist) return null;
  const entry = dist[profile];
  if (!entry) return null;
  return entry as ResourceEntry;
}
